/**
 * Hook WL — Configuração do Widget de Suporte Interno + Acesso Remoto (WebRTC)
 *
 * Toda I/O é feita 100% server-side via RPCs exclusivas do painel WL:
 *   - wl_get_support_widget()
 *   - wl_update_support_widget(...)
 *
 * Espelha o `useSupportWidgetConfig` do admin, porém com gate de permissão WL.
 *
 * @version 1.0.0
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';

const logger = createLogger('WlSupportWidget');

/**
 * Tipagem da configuração do widget (mesmo shape do admin).
 */
export interface WlSupportWidgetSettings {
  enabled: boolean;
  tenant_id: string | null;
  inbox_id: string | null;
  tenant_name: string | null;
  inbox_name: string | null;
  remote_access_enabled: boolean;
}

const DEFAULT_SETTINGS: WlSupportWidgetSettings = {
  enabled: false,
  tenant_id: null,
  inbox_id: null,
  tenant_name: null,
  inbox_name: null,
  remote_access_enabled: false,
};

export function useWlSupportWidget() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Leitura via RPC WL
  const { data: config, isLoading, refetch } = useQuery({
    queryKey: ['wl', 'support_widget'],
    queryFn: async (): Promise<WlSupportWidgetSettings> => {
      logger.debug('Buscando configuração WL do widget de suporte');
      const { data, error } = await supabase.rpc('wl_get_support_widget');
      if (error) {
        logger.error('Erro ao buscar config WL', error);
        throw error;
      }
      // RPC retorna TABLE → array com um único row
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return DEFAULT_SETTINGS;
      return {
        enabled: !!row.enabled,
        tenant_id: row.tenant_id ?? null,
        inbox_id: row.inbox_id ?? null,
        tenant_name: row.tenant_name ?? null,
        inbox_name: row.inbox_name ?? null,
        remote_access_enabled: !!row.remote_access_enabled,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Atualização via RPC WL
  const updateConfig = useMutation({
    mutationFn: async (next: Partial<WlSupportWidgetSettings>) => {
      const current = config || DEFAULT_SETTINGS;
      const merged: WlSupportWidgetSettings = { ...current, ...next };

      logger.info('Atualizando config WL', merged);

      const { error } = await supabase.rpc('wl_update_support_widget', {
        p_enabled: merged.enabled,
        p_tenant_id: merged.tenant_id,
        p_inbox_id: merged.inbox_id,
        p_tenant_name: merged.tenant_name,
        p_inbox_name: merged.inbox_name,
        p_remote_access_enabled: merged.remote_access_enabled,
      });
      if (error) {
        logger.error('Falha ao atualizar config WL', error);
        throw error;
      }
      return merged;
    },
    onSuccess: (data) => {
      // Atualiza cache WL e invalida cache do admin (mesma fonte: system_settings)
      queryClient.setQueryData(['wl', 'support_widget'], data);
      queryClient.invalidateQueries({ queryKey: ['system-settings', 'support_widget'] });
      toast({
        title: 'Configuração salva',
        description: 'Widget de suporte atualizado com sucesso.',
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Erro ao salvar',
        description: err.message || 'Não foi possível salvar a configuração.',
        variant: 'destructive',
      });
    },
  });

  return {
    config: config || DEFAULT_SETTINGS,
    isLoading,
    refetch,
    updateConfig: updateConfig.mutateAsync,
    isUpdating: updateConfig.isPending,
  };
}
